<template>
  <v-layout
    column
    id="inbox"
    class="overflow-y-hidden"
    style="position: relative;"
  >
    <v-flex>
      <v-toolbar dense>
        <v-toolbar-items>
          <v-dialog v-model="composeDialog">
            <template slot="activator" slot-scope="{ on }">
              <v-btn flat v-on="on" @click="getAllRecipients">
                <v-icon>create</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title><h2>New Message</h2></v-card-title>

              <v-card-text>
                <v-autocomplete
                  v-if="filters == false"
                  prepend-icon="search"
                  multiple
                  allow-overflow
                  clearable
                  chips
                  deletable-chips
                  v-model="newMessageTo"
                  :items="groupedRecipients"
                  :loading="fetchingGroupedRecipients"
                  :disabled="fetchingGroupedRecipients"
                  item-text="displayName"
                  item-value="identifier"
                  label="To"
                ></v-autocomplete>
                <div v-if="user.managerOrHigher">
                  <v-switch
                    v-model="filters"
                    :label="`Use Position & Location Filters`"
                    @change="this.clearToField"
                  ></v-switch>
                  <v-select
                    v-if="filters == true"
                    label="Position"
                    :items="positionFilters"
                    item-text="name"
                    item-value="id"
                    v-model="selectedPositionFilter"
                    :loading="positionsLoading || fetchingEmployeeRecipients"
                    :disabled="positionsLoading || fetchingEmployeeRecipients"
                    clearable
                  ></v-select>

                  <v-autocomplete
                    v-if="filters == true"
                    label="Location"
                    :items="locationFilters"
                    item-text="name"
                    item-value="id"
                    v-model="selectedLocationFilter"
                    :loading="locationsLoading || fetchingEmployeeRecipients"
                    :disabled="locationsLoading || fetchingEmployeeRecipients"
                    clearable
                  ></v-autocomplete>
                </div>
                <v-text-field
                  v-model="newMessageSubject"
                  label="Subject"
                ></v-text-field>

                <v-textarea
                  v-model="newMessageBody"
                  label="Message"
                ></v-textarea>

                <v-card-actions>
                  <v-checkbox
                    v-if="user.managerOrHigher"
                    v-model="copySMS"
                    label="Copy to SMS"
                  />
                  <v-spacer></v-spacer>
                  <v-btn color="grey" flat @click="discardMessage()"
                    >Discard</v-btn
                  >
                  <v-btn
                    color="primary"
                    @click.prevent="sendMessage()"
                    :disabled="!composeFieldsPresent"
                    :loading="messageSending"
                  >
                    Send
                  </v-btn>
                </v-card-actions>

                <v-layout column v-if="filters" class="subtle">
                  <v-flex>
                    Your message will go to {{ filteredEmployees.length }}
                    employees:
                  </v-flex>
                  <v-flex>
                    <v-layout row wrap>
                      <v-chip v-for="e in filteredEmployees">
                        {{ e.display_name }}
                      </v-chip>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-dialog>
          <v-btn v-if="!viewingArchive" flat @click="viewArchive"
            >Go To Archive</v-btn
          >
          <v-btn v-if="viewingArchive" flat @click="viewInbox"
            >Go To Inbox</v-btn
          >
          <v-btn flat @click="viewSent">Go To Sent</v-btn>

          <v-dialog v-model="oooDialog">
            <template slot="activator" slot-scope="{ on }">
              <v-btn
                flat
                v-on="on"
                :color="outOfOfficeStatus ? 'red darken-1' : ''"
              >
                <v-icon v-if="outOfOfficeStatus">check</v-icon>
                Out of Office
                <v-chip small v-if="outOfOfficeStatus" color="red darken-1"
                  >Enabled</v-chip
                >
              </v-btn>
            </template>
            <v-card>
              <v-card-text>
                <h2>Out of Office</h2>
                <v-checkbox
                  v-model="outOfOfficeStatus"
                  label="Are you out of office?"
                />
                <v-textarea
                  v-model="newOOOMessageBody"
                  label="Auto Reply Message"
                ></v-textarea>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="grey" flat @click="discardOOODialog()"
                    >Discard</v-btn
                  >
                  <v-btn
                    color="primary"
                    @click.prevent="saveOOOMessage()"
                    :disabled="!composeOOOFieldsPresent"
                    :loading="messageOOOSending"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-toolbar-items>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn v-if="unreadRequests > 0" flat @click="viewRequests">
            <v-badge color="red" right>
              <template slot="badge">
                <span>{{ unreadRequests }}</span>
              </template>
              Requests
            </v-badge>
          </v-btn>
          <v-btn v-else flat @click="viewRequests">
            Requests
          </v-btn>

          <v-text-field
            v-show="!viewingArchive"
            v-model="inboxSearch"
            append-icon="search"
          />
          <v-text-field
            v-show="viewingArchive"
            v-model="archiveSearch"
            append-icon="search"
          />
        </v-toolbar-items>
      </v-toolbar>
    </v-flex>

    <v-flex id="inbox-messages">
      <v-layout row justify-space-between align-center class="my-4">
        <v-flex>
          <h1 class="my-2">{{ viewingArchive ? "Archive" : "Inbox" }}</h1>
        </v-flex>

        <v-flex v-if="!viewingArchive" justify-self-end class="text-xs-right">
          <v-pagination v-model="inboxPage" :length="inboxPageLength" />
        </v-flex>

        <v-flex v-if="viewingArchive" justify-self-end class="text-xs-right">
          <v-pagination v-model="archivePage" :length="archivePageLength">
          </v-pagination>
        </v-flex>
      </v-layout>

      <div v-if="!viewingArchive">
        <h3 v-if="inboxSearch" class="my-2 grey--text">
          Messages matching '{{ inboxSearch }}'...
        </h3>
      </div>
      <div v-else>
        <h3 v-if="archiveSearch" class="my-2 grey--text">
          Messages matching '{{ archiveSearch }}'...
        </h3>
      </div>

      <v-progress-linear indeterminate v-if="searching" />
      <v-list v-else two-line style="height: 70vh; overflow-y: scroll;">
        <template v-for="(message, i) in messageList">
          <v-layout
            v-if="viewedMessage && message.id == viewedMessage.id"
            class="py-2 grey lighten-5"
            column
          >
            <v-flex>
              <v-toolbar
                dense
                class="white"
                :class="[
                  `${viewedMessage.systemMessage ? 'blue lighten-5' : ''}`
                ]"
              >
                <v-toolbar-side-icon @click="collapseMessage">
                  <v-icon>unfold_less</v-icon>
                </v-toolbar-side-icon>

                <v-toolbar-items> </v-toolbar-items>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                  <v-tooltip top>
                    <v-btn
                      v-if="!viewedMessage.archived"
                      flat
                      slot="activator"
                      @click="archive(viewedMessage)"
                    >
                      <v-icon>archive</v-icon>
                    </v-btn>
                    Archive
                  </v-tooltip>
                  <v-tooltip top>
                    <v-btn
                      v-if="viewedMessage.archived"
                      flat
                      slot="activator"
                      @click="sendToInbox(viewedMessage)"
                    >
                      <v-icon>move_to_inbox</v-icon>
                    </v-btn>
                    Send to Inbox
                  </v-tooltip>
                  <v-tooltip top>
                    <v-btn
                      flat
                      slot="activator"
                      @click="markUnread(viewedMessage)"
                    >
                      <v-icon>markunread</v-icon>
                    </v-btn>
                    Mark Unread
                  </v-tooltip>
                  <v-tooltip top>
                    <v-btn
                      flat
                      @click="toggleFlagged(message)"
                      :class="{ 'orange--text': message.flagged }"
                      slot="activator"
                    >
                      <v-icon>flag</v-icon>
                    </v-btn>
                    Flag
                  </v-tooltip>
                  <v-tooltip top>
                    <NewTaskDialog
                      slot="activator"
                      :managed="user.managerOrHigher"
                      :initialDescription="
                        `${message.subject} ${message.message}`
                      "
                    >
                      <v-btn flat>
                        <v-icon>check_circle </v-icon>
                      </v-btn>
                    </NewTaskDialog>
                    Create Task
                  </v-tooltip>
                </v-toolbar-items>
              </v-toolbar>
            </v-flex>

            <v-flex
              class="pa-4 white"
              :class="{
                'flagged-border': message.flagged,
                'important-border': message.important
              }"
            >
              <h2>{{ viewedMessage.subject }}</h2>
              <h3 class="message-details font-weight-regular mb-2">
                <span class="subtle font-weight-regular">From</span>
                {{ viewedMessage.sender.displayName }}
                <span class="subtle font-weight-regular"
                  >at
                  {{
                    viewedMessage.sentAt | moment("h:mma on dddd, MMMM Do")
                  }}</span
                >
              </h3>
              <div
                style="white-space: pre-wrap"
                v-html="viewedMessage.message"
              ></div>
            </v-flex>

            <v-flex class="pa-4 white" v-if="replyingToMessage">
              <v-textarea
                id="message-reply-input"
                v-model="newMessageBody"
                label="Your reply"
                outline
                autogrow
                rows="15"
              ></v-textarea>
            </v-flex>

            <v-flex>
              <v-toolbar
                dense
                flat
                class="white"
                :class="{ 'flagged-border': message.flagged }"
              >
                <v-toolbar-items> </v-toolbar-items>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                  <div v-if="viewedMessage.replyable">
                    <v-btn
                      v-if="!replyingToMessage"
                      flat
                      color="primary"
                      @click="replyTo(viewedMessage)"
                      >Reply</v-btn
                    >
                  </div>
                  <v-btn
                    v-if="replyingToMessage"
                    flat
                    color="secondary"
                    @click="discardMessage()"
                    >Discard</v-btn
                  >
                  <v-btn
                    v-if="replyingToMessage"
                    color="primary"
                    :disabled="!composeFieldsPresent"
                    :loading="messageSending"
                    @click="sendMessage()"
                    >Send</v-btn
                  >
                </v-toolbar-items>
              </v-toolbar>
            </v-flex>
          </v-layout>

          <v-list-tile
            v-else
            :class="messageClasses(message)"
            @click="viewMessage(message)"
            class="message-in-list px-1"
          >
            <v-icon class="hover-expand">
              unfold_more
            </v-icon>
            <v-list-tile-content>
              <v-list-tile-title>
                <v-layout row justify-space-between>
                  <v-flex
                    grow
                    :class="`${!message.read ? 'font-weight-bold' : ''}`"
                  >
                    <!-- TODO?
                    <div class="d-inline-block" v-if="message.messagesInThread > 2">
                      ({{ message.messagesInThread }})
                    </div>
                    -->
                    <v-icon
                      v-if="viewingArchive && message.sender.id == user.id"
                      class="mr-2"
                      >send</v-icon
                    >{{ truncate(message.subject, 80) }}
                  </v-flex>
                  <v-flex shrink>
                    <div
                      v-if="
                        $moment(message.sentAt).isBefore(
                          $moment().startOf('day')
                        )
                      "
                    >
                      {{ message.sentAt | moment("dddd, MMMM Do") }}
                    </div>
                    <div v-else>
                      {{ message.sentAt | moment("h:mm a") }}
                    </div>
                  </v-flex>
                </v-layout>
              </v-list-tile-title>

              <v-list-tile-sub-title>
                <span class="black--text">{{
                  message.sender.displayName
                }}</span>
                - {{ message.message }}
              </v-list-tile-sub-title>
            </v-list-tile-content>

            <div @click.stop class="inbox-tools white">
              <div v-if="viewingArchive">
                <v-btn
                  v-if="message.archived"
                  small
                  icon
                  @click="sendToInbox(message)"
                >
                  <v-icon>move_to_inbox</v-icon>
                </v-btn>
              </div>
              <div v-else>
                <v-btn
                  v-if="!message.archived"
                  small
                  icon
                  @click="archive(message)"
                >
                  <v-icon>archive</v-icon>
                </v-btn>
                <v-btn
                  v-if="!message.read"
                  small
                  icon
                  @click="markRead(message)"
                >
                  <v-icon size="18">drafts</v-icon>
                </v-btn>
                <v-btn
                  v-if="message.read"
                  small
                  icon
                  @click="markUnread(message)"
                >
                  <v-icon size="18">markunread</v-icon>
                </v-btn>
                <v-btn
                  small
                  icon
                  @click="toggleFlagged(message)"
                  :class="{ 'orange--text': message.flagged }"
                >
                  <v-icon size="18">flag</v-icon>
                </v-btn>
                <NewTaskDialog
                  slot="activator"
                  :managed="user.managerOrHigher"
                  :initialDescription="`${message.subject} ${message.message}`"
                >
                  <v-btn flat small icon>
                    <v-icon size="18">check_circle </v-icon>
                  </v-btn>
                </NewTaskDialog>
              </div>
            </div>
          </v-list-tile>
          <v-divider></v-divider>
        </template>
      </v-list>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import moment from "moment";
import collectionApi from "@/api/collections";
import outOfOfficeApi from "@/api/outOfOffice";
import NewTaskDialog from "../todos/new-todo-item-dialog.vue";

export default {
  components: {
    NewTaskDialog
  },
  created() {
    this.fetchData();
    this.setUp();
  },
  computed: {
    ...mapState("userAuth", ["user"]),
    ...mapState("messages", [
      "inboxMessages",
      "archiveMessages",
      "employeeRecipients"
    ]),
    ...mapGetters("messages", [
      "recentFirstInbox",
      "recentFirstArchive",
      "groupedRecipients"
    ]),
    inboxPageLength: function() {
      return Math.ceil(this.inboxQueryTotal / this.limit);
    },
    archivePageLength: function() {
      return Math.ceil(this.archiveQueryTotal / this.limit);
    },
    recipients() {},
    messageList() {
      if (!this.viewingArchive) {
        return this.recentFirstInbox;
      } else {
        return this.recentFirstArchive;
      }
    },
    composeFieldsPresent() {
      if (this.replyingToMessage) {
        if (this.newMessageBody) {
          return true;
        }
      }
      if (this.newMessageTo && this.newMessageBody) {
        return true;
      }
      return false;
    },
    composeOOOFieldsPresent() {
      // Anything is valid if the checkbox is unchecked
      if (!this.outOfOfficeStatus) {
        return true;
      }
      // BUT, if the checkbox is checked, we need a message body
      if (this.outOfOfficeStatus) {
        if (this.newOOOMessageBody) {
          return true;
        }
      }
      return false;
    },
    filteredEmployees() {
      var employees = [];
      if (this.employeeObjects.length <= 0) {
        // wait for all employees to load
        return employees;
      }

      if (this.selectedPositionFilter && this.selectedLocationFilter) {
        // both filters
        employees = this.employeeObjects.filter(eo => {
          // only the employees fitting BOTH
          if (
            eo.position_ids.includes(this.selectedPositionFilter) &&
            eo.worksite_ids.includes(this.selectedLocationFilter)
          ) {
            return eo.id;
          }
        });
      } else if (this.selectedLocationFilter) {
        // only location filter
        employees = this.employeeObjects.filter(eo => {
          if (eo.worksite_ids.includes(this.selectedLocationFilter)) {
            return eo.id;
          }
        });
      } else if (this.selectedPositionFilter) {
        // only position filter
        employees = this.employeeObjects.filter(eo => {
          if (eo.position_ids.includes(this.selectedPositionFilter)) {
            return eo.id;
          }
        });
      }
      return employees;
    }

    //filteredInboxMessages() {
    //// TODO: search on server?
    //if (!this.inboxSearch) {
    //return this.recentFirstInbox;
    //} else {
    //const search = this.inboxSearch.toLowerCase();

    //// do api call for search and set messages to this

    //// debounce
    //// cancel pending call
    //clearTimeout(this._inboxSearchTimer)

    //// delay new call 500ms
    //this._inboxSearchTimer = setTimeout(() => {
    //this.searchInbox()
    //}, 500)

    //// Hide for now
    ////return this.recentFirstInbox.filter(function(message) {
    ////const subject = message.subject ? message.subject : '';
    ////return (
    ////subject.toLowerCase().indexOf(search) > -1 ||
    ////message.message.toLowerCase().indexOf(search) > -1 ||
    ////message.sender.displayName.toLowerCase().indexOf(search) > -1
    ////);
    ////});
    //}
    //},
    //filteredArchiveMessages() {
    //if (!this.archiveSearch) {
    //return this.recentFirstArchive;
    //} else {
    //const search = this.archiveSearch.toLowerCase();
    //return this.recentFirstArchive.filter(function(message) {
    //const subject = message.subject ? message.subject : '';
    //return (
    //subject.toLowerCase().indexOf(search) > -1 ||
    //message.message.toLowerCase().indexOf(search) > -1 ||
    //message.sender.displayName.toLowerCase().indexOf(search) > -1
    //);
    //});
    //}
    //},
  },

  watch: {
    /* when the filter-sending option is turned on and off, we should
         reset the state of the _unused_ one */
    filters: function(val) {
      if (val) {
        // using position & location filters
        this.newMessageTo = [];
      } else {
        // using 'To' field directly
        this.selectedLocationFilter = null;
        this.selectedPositionFilter = null;
      }
    },

    inboxSearch: function(search) {
      if (!search) {
        this.inboxPage = 1;
        this.doSearchInbox();
      }
      this.inboxPage = 1;
      this.searchInboxDebounced();
    },

    archiveSearch: function(search) {
      if (!search) {
        this.archivePage = 1;
        this.doSearchArchive();
      }
      this.archivePage = 1;
      this.searchArchiveDebounced();
    },

    inboxPage: function(val) {
      this.doSearchInbox();
    },
    archivePage: function(val) {
      this.doSearchArchive();
    }
  },
  data() {
    return {
      copySMS: false,
      outOfOfficeStatus: false,
      outOfOffice: {},
      unreadRequests: 0,

      inboxPage: 1,
      archivePage: 1,

      inboxQueryTotal: 1,
      archiveQueryTotal: 1,

      searching: false,

      limit: 40,

      inboxSearch: "",
      archiveSearch: "",

      viewingArchive: false,
      viewedMessage: null,
      composeDialog: false,
      oooDialog: false,
      messageSending: false,
      messageOOOSending: false,
      replyingToMessage: null,
      newMessageTo: [],
      newMessageSubject: null,
      newMessageBody: null,
      newOOOMessageBody: null,
      newMessageInReplyToId: null,
      employeeObjects: [],

      fetchingEmployeeRecipients: false,
      fetchingGroupedRecipients: false,

      positionsLoading: false,
      locationsLoading: false,

      positionFilters: [],
      selectedPositionFilter: null,
      employeesInSelectedPosition: [],

      locationFilters: [],
      selectedLocationFilter: null,
      employeesInSelectedLocation: [],

      positionRecipients: [],

      filters: false
    };
  },
  methods: {
    setUp: function() {
      this.positionsLoading = true;
      collectionApi.getPositions(
        positions => {
          this.positionFilters = positions;
          this.positionsLoading = false;
        },
        error => {
          console.log(error);
          this.positionsLoading = false;
        }
      );

      this.locationsLoading = true;
      collectionApi.getLocations(
        locations => {
          this.locationFilters = locations;
          this.locationsLoading = false;
        },
        error => {
          console.log(error);
          this.locationsLoading = false;
        }
      );

      outOfOfficeApi.get(
        response => {
          this.saveOOOData(response.ooo);
        },
        error => {
          console.log(error);
        }
      );
    },

    doSearchInbox() {
      this.$store
        .dispatch("messages/searchInbox", {
          page: this.inboxPage,
          limit: this.limit,
          searchTerm: this.inboxSearch
        })
        .then(res => {
          this.searching = false;
          this.inboxQueryTotal = res.queryTotal;
        });
    },

    doSearchArchive() {
      this.$store
        .dispatch("messages/searchArchive", {
          page: this.archivePage,
          limit: this.limit,
          searchTerm: this.archiveSearch
        })
        .then(res => {
          this.searching = false;
          this.archiveQueryTotal = res.queryTotal;
        });
    },

    searchInboxDebounced() {
      this.inboxSearch = this.inboxSearch.toLowerCase();

      this.searching = true;
      // debounce
      // cancel pending call
      clearTimeout(this._inboxSearchTimer);

      // delay new call 500ms
      this._inboxSearchTimer = setTimeout(() => {
        this.doSearchInbox();
      }, 500);
    },

    searchArchiveDebounced() {
      this.archiveSearch = this.archiveSearch.toLowerCase();

      this.searching = true;
      // debounce
      // cancel pending call
      clearTimeout(this._archiveSearchTimer);

      // delay new call 500ms
      this._archiveSearchTimer = setTimeout(() => {
        this.doSearchArchive();
      }, 500);
    },

    clearToField() {
      this.newMessageTo = [];
    },

    matchLocationToRecipient(selectedLocationFilter) {
      this.employeesInSelectedLocation = this.employeeObjects.filter(eo => {
        if (eo.worksite_ids.includes(selectedLocationFilter)) {
          return eo.id;
        }
      });
      if (this.newMessageTo === null || this.newMessageTo.length === 0) {
        this.newMessageTo = this.employeesInSelectedLocation;
      } else {
        this.newMessageTo = this.newMessageTo.filter(e =>
          this.employeesInSelectedLocation.includes(e)
        );
        // this.newMessageTo = this.newMessageTo.concat(this.employeesInSelectedLocation)
      }
    },

    messageClasses(message) {
      return {
        "blue lighten-4": message.systemMessage && !message.read,
        "blue lighten-5": message.systemMessage && message.read,
        "grey lighten-4": !message.systemMessage && message.read,
        "flagged-border": message.flagged,
        "important-border": message.important
      };
    },
    fetchData() {
      this.$store
        .dispatch("messages/getInbox", { page: 1, limit: this.limit })
        .then(res => {
          this.inboxQueryTotal = res.queryTotal;
          if (this.$route.query.messageId) {
            const messageId = this.$route.query.messageId;
            const message = this.inboxMessages.find(m => m.id == messageId);
            if (message) {
              this.setViewedMessage(message);
            } else {
              this.$router.push({ query: {} });
            }
          }
        });
      this.$store.dispatch("messages/getUnreadRequestCount").then(res => {
        this.unreadRequests = res.count;
      });
    },

    getAllRecipients() {
      this.getGroupedRecipients();
      // The full employee list feeds the position and location filters,
      // which only staff are offered (and the server refuses it to anyone else).
      if (this.user.managerOrHigher) {
        this.getEmployeeRecipients();
      }
    },

    getEmployeeRecipients() {
      this.fetchingEmployeeRecipients = true;
      this.$store
        .dispatch("messages/getEmployeeRecipients")
        .then(res => {
          this.employeeObjects = this.employeeRecipients;
        })
        .finally(res => {
          this.fetchingEmployeeRecipients = false;
        });
    },

    getGroupedRecipients() {
      this.fetchingGroupedRecipients = true;
      this.$store
        .dispatch("messages/getGroupedRecipients")
        .then(res => {})
        .finally(res => {
          this.fetchingGroupedRecipients = false;
        });
    },

    viewArchive() {
      this.fetchArchivePage();
      this.viewingArchive = true;
    },
    fetchInboxPage() {
      this.doSearchInbox();
      //this.$store
      //.dispatch('messages/getInbox', {
      //page: this.inboxPage,
      //limit: this.limit,
      //})
      //.then(res => {
      //this.inboxQueryTotal = res.queryTotal;
      //});
    },
    fetchArchivePage() {
      this.$store
        .dispatch("messages/getArchive", {
          page: this.archivePage,
          limit: this.limit
        })
        .then(res => {
          this.archiveQueryTotal = res.queryTotal;
        });
    },
    viewInbox() {
      this.fetchInboxPage();
      this.viewingArchive = false;
    },
    viewSent() {
      this.$router.replace({ name: "outbox" });
    },
    viewRequests() {
      this.$router.replace({ name: "requests" });
    },

    replyTo(message) {
      this.replyingToMessage = message;
      const quotedMessage = message.message
        .split("\n")
        .map(line => `> ${line}`)
        .join("\n");
      const quotedTime = this.$moment(message.sentAt).format(
        "dddd, MMMM Do, h:mm a"
      );
      const quoteExplanation = `On ${quotedTime}, ${message.sender.displayName} wrote:\n`;
      this.newMessageBody = `\n\n${quoteExplanation}\n${quotedMessage}`;
      if (message.in_reply_to_message_id) {
        this.newMessageSubject = message.subject;
      } else {
        this.newMessageSubject = `Re: ${message.subject}`;
      }
      this.newMessageTo = [message.sender.id];
      this.newMessageInReplyToId = message.id;
      // document.getElementById("message-reply-input").focus();
      // document.getElementById("message-reply-input").setSelectionRange(0,0);
    },
    sendMessage() {
      this.messageSending = true;
      let recipientIds;
      if (this.filters) {
        // using filtered send
        recipientIds = this.filteredEmployees.map(r => r.id);
      } else {
        // using 'To' field directly
        recipientIds = this.groupedRecipients
          .filter(r => {
            if (this.newMessageTo.includes(r.identifier)) {
              return r;
            }
          })
          .map(r => r.ids)
          .flat(Infinity);
      }
      this.$store
        .dispatch("messages/send", {
          inReplyToMessageId: this.newMessageInReplyToId,
          senderId: this.user.id,
          recipientIds: recipientIds,
          copySMS: this.copySMS,
          subject: this.newMessageSubject,
          message: this.newMessageBody
        })
        .then(res => {
          this.$store.dispatch("global/showSnackbarSuccess", {
            message: "Message sent!"
          });
        })
        .catch(res => {
          this.$store.dispatch("global/showSnackbarError", {
            message: "Something went wrong with that message"
          });
        })
        .finally(res => {
          this.messageSending = false;
          this.copySMS = false;
          this.discardMessage();
          this.fetchData();
        });
    },
    saveOOOMessage() {
      outOfOfficeApi.create(
        {
          out_of_office: {
            message: this.newOOOMessageBody,
            status: this.outOfOfficeStatus
          }
        },
        response => {
          this.saveOOOData(response);
        },
        error => {
          console.log(error);
        }
      );

      this.discardOOODialog();
    },
    discardMessage() {
      this.replyingToMessage = null;
      this.newMessageTo = null;
      this.newMessageSubject = null;
      this.newMessageBody = null;
      this.composeDialog = false;
      this.selectedPositionFilter = null;
      this.selectedLocationFilter = null;
      this.filters = false;
    },
    discardOOODialog() {
      this.oooDialog = false;
      this.newOOOMessageBody = this.outOfOffice.message;
      this.outOfOfficeStatus = this.outOfOffice.status;
    },
    saveOOOData(ooo) {
      this.outOfOffice = ooo;
      this.newOOOMessageBody = ooo.message;
      this.outOfOfficeStatus = ooo.status;
    },
    viewMessage(message) {
      if (!this.viewingArchive) {
        //this.$router.push({query: {messageId: message.id}});
        this.markRead(message);
      }
      this.setViewedMessage(message);
    },
    setViewedMessage(message) {
      this.viewedMessage = message;
    },
    collapseMessage() {
      // this.$router.push({ name: 'messages' })
      this.viewedMessage = null;
    },
    archive(message) {
      this.viewedMessage = null;
      this.$store.dispatch("messages/markArchived", message.id).finally(r => {
        this.$store.dispatch("messages/getUnreadCount");
      });
    },
    sendToInbox(message) {
      this.viewedMessage = null;
      this.$store.dispatch("messages/markInbox", message.id).finally(r => {
        this.$store.dispatch("messages/getUnreadCount");
      });
    },
    markRead(message) {
      if (!message.read) {
        this.$store.dispatch("messages/markRead", message.id).finally(r => {
          this.$store.dispatch("messages/getUnreadCount");
        });
      }
    },
    markUnread(message) {
      if (message.read) {
        this.$store.dispatch("messages/markUnread", message.id).finally(r => {
          this.$store.dispatch("messages/getUnreadCount");
        });
      }
      this.collapseMessage();
    },
    toggleFlagged(message) {
      if (message.flagged) {
        this.$store.dispatch("messages/markUnflagged", message.id);
      } else {
        this.$store.dispatch("messages/markFlagged", message.id);
      }
    },
    truncate(message, chars) {
      if (message && message.length > chars) {
        return `${message.substring(0, chars - 2)}...`;
      } else if (!message) {
        return "<No Subject>";
      } else {
        return message;
      }
    }
  }
};
</script>

<style scoped>
.hover-expand {
  display: none;
  position: absolute;
  left: -6px;
}
.inbox-tools {
  opacity: 1;
  display: none;
  position: absolute;
  right: 185px;
  bottom: 0px;
}
.message-in-list:hover .hover-expand {
  display: block;
}
.message-in-list:hover .inbox-tools {
  display: block;
}
.message-in-list.flagged-border {
  border-left: 2px solid orange !important;
}
.message-in-list.important-border {
  border-left: 4px solid red !important;
}

.flagged-border {
  border-left: 2px solid orange !important;
}
.important-border {
  border-left: 4px solid red !important;
}
</style>

<style></style>
